import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useDateRange } from "@/context/DateRangeContext";
import { fmtMoney, fmtMonth, fmtNum } from "@/lib/format";
import StatCard from "@/components/StatCard";
import ProductThumb from "@/components/ProductThumb";
import { EmptyState, Loading, ChartTooltip } from "@/components/shared";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { DollarSign, Wallet, Receipt, TrendingDown } from "lucide-react";

function FeeBreakdown({ currency, start, end }) {
  const [dim, setDim] = useState("marketplace");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/analytics/fees-breakdown?currency=${currency}&start=${start}&end=${end}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [currency, start, end]);
  if (!data) return null;
  const DIMS = [{ id: "marketplace", label: "By Marketplace" }, { id: "country", label: "By Country" }, { id: "monthly", label: "By Month" }];
  const rows = data[dim] || [];
  return (
    <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="fee-breakdown">
      <div className="p-5 border-b border-[#1a1a1a] flex items-center justify-between flex-wrap gap-3">
        <div><h3 className="font-head font-bold text-white">Fees & Charges Breakdown</h3><p className="text-xs text-[#666]">Referral, FBA & other fees split by dimension{data.estimated ? " · includes estimates where settlement is missing" : ""}</p></div>
        <div className="flex items-center gap-1 bg-[#111] border border-[#1a1a1a] rounded-full p-1">
          {DIMS.map((d) => (
            <button key={d.id} data-testid={`fee-dim-${d.id}`} onClick={() => setDim(d.id)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${dim === d.id ? "bg-[#007AFF] text-white" : "text-[#A3A3A3] hover:text-white"}`}>{d.label}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
            <th className="px-5 py-3">{dim === "country" ? "Country" : dim === "monthly" ? "Month" : "Marketplace"}</th>
            <th className="px-5 py-3 text-right">Orders</th><th className="px-5 py-3 text-right">Revenue</th>
            <th className="px-5 py-3 text-right">Referral</th><th className="px-5 py-3 text-right">FBA</th><th className="px-5 py-3 text-right">Other</th>
            <th className="px-5 py-3 text-right">Total Fees</th><th className="px-5 py-3 text-right">Fee %</th><th className="px-5 py-3 text-right">Net Profit</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-testid={`fee-row-${r.key}`} className="border-t border-[#141414] hover:bg-white/5">
                <td className="px-5 py-3 text-white font-medium">{dim === "monthly" ? fmtMonth(r.key) : r.name}</td>
                <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtNum(r.orders)}</td>
                <td className="px-5 py-3 text-right tnum text-white">{fmtMoney(r.revenue, currency, true)}</td>
                <td className="px-5 py-3 text-right tnum text-[#4da3ff]">{fmtMoney(r.referral, currency, true)}</td>
                <td className="px-5 py-3 text-right tnum text-[#a78bfa]">{fmtMoney(r.fba, currency, true)}</td>
                <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtMoney(r.other, currency, true)}</td>
                <td className="px-5 py-3 text-right tnum text-[#F59E0B] font-semibold">{fmtMoney(r.total_fees, currency, true)}</td>
                <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{r.fee_pct}%</td>
                <td className="px-5 py-3 text-right tnum font-semibold" style={{ color: r.net_profit >= 0 ? "#10B981" : "#EF4444" }}>{fmtMoney(r.net_profit, currency, true)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="px-5 py-8 text-center text-[#666]">No fee data for this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PerfTable({ title, subtitle, rows, currency }) {
  return (
    <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden">
      <div className="p-5 border-b border-[#1a1a1a]"><h3 className="font-head font-bold text-white">{title}</h3><p className="text-xs text-[#666]">{subtitle}</p></div>
      <div className="divide-y divide-[#141414]">
        {rows.map((p) => (
          <div key={p.sku} data-testid={`cp-perf-${p.sku}`} className="flex items-center gap-3 px-5 py-3">
            <ProductThumb src={p.image} size={36} title={p.product_name} />
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm truncate" title={p.product_name}>{p.product_name}</p>
              <p className="text-[#666] text-xs tnum truncate">{p.sku} · {fmtNum(p.units)} units</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold tnum" style={{ color: p.net_profit >= 0 ? "#10B981" : "#EF4444" }}>{fmtMoney(p.net_profit, currency, true)}</p>
              <p className="text-xs tnum" style={{ color: p.margin >= 0 ? "#A3A3A3" : "#EF4444" }}>{p.margin}% margin</p>
            </div>
          </div>
        ))}
        {!rows.length && <div className="px-5 py-8 text-center text-[#666] text-sm">No data</div>}
      </div>
    </div>
  );
}

export default function CostProfitTab() {
  const { currency } = useCurrency();
  const { start, end } = useDateRange();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setLoading(true);
    const dr = `&start=${start}&end=${end}`;
    Promise.all([
      api.get(`/analytics/cost-profit?currency=${currency}${dr}`),
      api.get(`/analytics/products?currency=${currency}${dr}`),
    ]).then(([c, p]) => {
      setData(c.data);
      setProducts(p.data.data);
      setLoading(false);
    });
  }, [currency, start, end]);

  if (loading) return <Loading />;
  if (!data || !data.monthly.length) {
    return <EmptyState title="No cost data" message="Upload orders or load sample data to see cost and profit breakdowns." />;
  }

  const b = data.breakdown;
  const ranked = [...products].filter((p) => p.units > 0).sort((a, c) => c.net_profit - a.net_profit);
  const top = ranked.slice(0, 5);
  const bottom = ranked.slice(-5).reverse();
  const pie = [
    { name: "Net Profit", value: Math.max(b.net_profit, 0), color: "#10B981" },
    { name: "COGS", value: b.cogs, color: "#F59E0B" },
    { name: "Referral Fees", value: b.referral_fees, color: "#007AFF" },
    { name: "FBA Fees", value: b.fba_fees, color: "#8B5CF6" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="cp-revenue" label="Revenue" value={fmtMoney(b.revenue, currency, true)} icon={DollarSign} accent="#007AFF" />
        <StatCard testid="cp-cogs" label="COGS" value={fmtMoney(b.cogs, currency, true)} icon={Receipt} accent="#F59E0B" />
        <StatCard testid="cp-fees" label="Amazon Fees" value={fmtMoney(b.referral_fees + b.fba_fees, currency, true)} sub="referral + FBA" icon={TrendingDown} accent="#8B5CF6" />
        <StatCard testid="cp-net" label="Net Profit" value={fmtMoney(b.net_profit, currency, true)} icon={Wallet} accent="#10B981" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="profit-trend-chart">
          <h3 className="font-head font-bold text-white mb-1">Monthly Revenue vs Profit</h3>
          <p className="text-xs text-[#666] mb-4">Stacked cost structure per month</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.monthly.map((m) => ({ ...m, period: fmtMonth(m.period) }))} margin={{ left: -10, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#1a1a1a" }} />
              <YAxis tick={{ fill: "#666", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => fmtMoney(v, currency, true)} />
              <Tooltip cursor={{ fill: "#ffffff08" }} content={<ChartTooltip currency={currency} moneyKeys={["cogs", "fees", "net_profit"]} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="cogs" name="COGS" stackId="a" fill="#F59E0B" />
              <Bar dataKey="fees" name="Fees" stackId="a" fill="#8B5CF6" />
              <Bar dataKey="net_profit" name="Net Profit" stackId="a" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-5" data-testid="cost-breakdown-chart">
          <h3 className="font-head font-bold text-white mb-1">Where the money goes</h3>
          <p className="text-xs text-[#666] mb-4">Revenue allocation</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {pie.map((p, i) => <Cell key={i} fill={p.color} stroke="#0f0f0f" />)}
              </Pie>
              <Tooltip content={<ChartTooltip currency={currency} moneyKeys={["value"]} />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {pie.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-[#A3A3A3]">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />{p.name}
                </span>
                <span className="tnum text-white font-semibold">{fmtMoney(p.value, currency, true)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {ranked.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="cp-sku-performers">
          <PerfTable title="Top Performers" subtitle="Most profitable SKUs" rows={top} currency={currency} />
          <PerfTable title="Underperformers" subtitle="Lowest / negative profit SKUs" rows={bottom} currency={currency} />
        </div>
      )}
      <FeeBreakdown currency={currency} start={start} end={end} />
      <p className="text-xs text-[#555]">{data.estimated ? "Figures are estimated where settlement data is unavailable (15% referral + $3/unit FBA fee; COGS from inventory unit cost or 40% fallback)." : "Fees are actual, sourced from your uploaded Settlement/Payments report. COGS from inventory unit cost or 40% fallback."}</p>
    </div>
  );
}
